import { log, updateLoggerSessionContext, updateLoggerUserContext, warn } from "../utils/logger";
import { UserInfo } from "./types";

const SESSION_STORAGE_KEY = "l9rum_session";

export interface PersistedSession {
  id: string;
  previousId?: string;
  startedAt: number;
  lastActivityAt: number;
  /** Sampling decision for this session (persisted for cross-tab consistency) */
  sampled?: boolean;
}

const emitSessionStoreLog = (
  message: string,
  attributes: Record<string, unknown> = {},
) => {
  log({
    message,
    attributes: {
      "l9rum.session.component": "SessionStore",
      ...attributes,
    },
  });
};

let currentSessionId: string | undefined;
let previousSessionId: string | undefined;
let currentSessionStartedAt: number | undefined;
let currentSessionLastActivityAt: number | undefined;
let currentSessionSampled: boolean | undefined;
let currentUser: UserInfo | undefined;

function isLocalStorageSupported(): boolean {
  try {
    const testKey = "__l9rum_test__";
    localStorage.setItem(testKey, "1");
    localStorage.removeItem(testKey);
    return true;
  } catch {
    return false;
  }
}

function persistSessionToStorage() {
  if (!currentSessionId || !currentSessionStartedAt) {
    return;
  }
  if (!isLocalStorageSupported()) {
    return;
  }
  try {
    const data: PersistedSession = {
      id: currentSessionId,
      previousId: previousSessionId,
      startedAt: currentSessionStartedAt,
      lastActivityAt: currentSessionLastActivityAt ?? Date.now(),
      sampled: currentSessionSampled,
    };
    localStorage.setItem(SESSION_STORAGE_KEY, JSON.stringify(data));
  } catch (err) {
    // localStorage may be unavailable (private browsing, quota exceeded)
    warn({
      message: "[L9RUM Sessions] Failed to persist session to localStorage",
      attributes: { "l9rum.session.component": "SessionStore" },
      error: err,
    });
  }
}

function clearSessionFromStorage() {
  if (!isLocalStorageSupported()) {
    return;
  }
  try {
    localStorage.removeItem(SESSION_STORAGE_KEY);
  } catch {
    // Ignore errors when clearing
  }
}

export function getPersistedSession(): PersistedSession | undefined {
  if (!isLocalStorageSupported()) {
    return undefined;
  }
  try {
    const raw = localStorage.getItem(SESSION_STORAGE_KEY);
    if (!raw) {
      return undefined;
    }
    const data = JSON.parse(raw) as PersistedSession;
    if (data && typeof data.id === "string" && typeof data.startedAt === "number") {
      // Ensure lastActivityAt exists (for backward compatibility)
      if (typeof data.lastActivityAt !== "number") {
        data.lastActivityAt = data.startedAt;
      }
      return data;
    }
    return undefined;
  } catch {
    return undefined;
  }
}

export function setCurrentSessionId(id: string | undefined) {
  currentSessionId = id;
  updateLoggerSessionContext(currentSessionId, previousSessionId);
  if (id) {
    persistSessionToStorage();
  } else {
    clearSessionFromStorage();
  }
}

export function setPreviousSessionId(id: string | undefined) {
  previousSessionId = id;
  updateLoggerSessionContext(currentSessionId, previousSessionId);
  persistSessionToStorage();
}

export function setSessionStartedAt(startedAt: number | undefined) {
  currentSessionStartedAt = startedAt;
  persistSessionToStorage();
}

export function getSessionStartedAt(): number | undefined {
  return currentSessionStartedAt;
}

export function setSessionLastActivityAt(lastActivityAt: number | undefined) {
  currentSessionLastActivityAt = lastActivityAt;
  persistSessionToStorage();
}

export function getSessionLastActivityAt(): number | undefined {
  return currentSessionLastActivityAt;
}

export function updateSessionActivity() {
  currentSessionLastActivityAt = Date.now();
  persistSessionToStorage();
}

export function setSessionSampled(sampled: boolean | undefined) {
  currentSessionSampled = sampled;
  persistSessionToStorage();
}

export function getSessionSampled(): boolean | undefined {
  return currentSessionSampled;
}

export function getCurrentSessionId() {
  return currentSessionId;
}

export function getPreviousSessionId() {
  return previousSessionId;
}

export function setCurrentUser(user: UserInfo | undefined) {
  const prevHadUser = !!currentUser;
  currentUser = user ? { ...user } : undefined;
  updateLoggerUserContext(currentUser);
  if (currentUser) {
    emitSessionStoreLog("[L9RUM Sessions] User set via identify", {
      "l9rum.session.user_keys": JSON.stringify(Object.keys(currentUser)),
    });
  } else if (prevHadUser) {
    emitSessionStoreLog("[L9RUM Sessions] User cleared via identify");
  }
}

export function getCurrentUser(): UserInfo | undefined {
  return currentUser ? { ...currentUser } : undefined;
}
